import {
  Battle_Action,
  Battle_Action_Dance,
  Battle_Action_Faint,
  Battle_Action_Move,
  Battle_Action_Turn,
  Battle_Action_UseSkill,
} from "types/battle-engine/battle";
import { ClientPokemon } from "./entities/client-pokemon";
import { MS_PER_TICK } from "types/battle-engine/constants";
import { BattleStatsRecordPerTick, BattleEvent } from "types/battle-engine";
import { VFX } from "./entities/client-vfx";
import { Application, Container, Text, Graphics } from "pixi.js";

/**
 * Handles chronological replay of battle actions from the battle log
 */
export class BattleReplay {
  private pokemons: Record<string, ClientPokemon>;
  private actionsByTick: Map<
    number,
    Array<{ pokemonId: string; action: Battle_Action }>
  >;
  private eventsByTick: Map<number, BattleEvent[]>;
  private onTickUpdate: ((tick: number) => void) | undefined;
  private onEvent: ((event: BattleEvent) => void) | undefined;
  private app: Application | null = null;
  private parent: Container | null = null;
  private overtimeTimerContainer: Container | null = null;
  private previousHP: Record<string, number> = {};
  private overtimeTimerOuterText: Text | null = null;
  private overtimeTimerInnerText: Text | null = null;

  private isShaking: boolean = false;
  private originalStagePosition: { x: number; y: number } = { x: 0, y: 0 };

  private manaBarA: Container | null = null;
  private manaBarB: Container | null = null;
  private manaBarAGraphics: Graphics | null = null;
  private manaBarBGraphics: Graphics | null = null;
  private manaBarAText: Text | null = null;
  private manaBarBText: Text | null = null;

  private isDestroyed: boolean = false;

  // Overtime constants (must match server)
  private readonly OVERTIME_START_TICK = 1500;

  constructor(
    pokemons: Record<string, ClientPokemon>,
    onTickUpdate?: (tick: number) => void,
    onEvent?: (event: BattleEvent) => void,
  ) {
    this.pokemons = pokemons;
    this.actionsByTick = new Map();
    this.eventsByTick = new Map();
    this.onTickUpdate = onTickUpdate;
    this.onEvent = onEvent;

    // Get app and parent from first pokemon
    const firstPokemon = Object.values(pokemons)[0];
    if (firstPokemon) {
      this.app = (firstPokemon as any).app;
      this.parent = (firstPokemon as any).parent;
    }

    const teamA = Object.values(this.pokemons).filter(
      (pokemon) => pokemon.Team === "A",
    );
    const teamB = Object.values(this.pokemons).filter(
      (pokemon) => pokemon.Team === "B",
    );

    for (const pokemon of teamA) {
      pokemon.init(teamB, teamA);
    }
    for (const pokemon of teamB) {
      pokemon.init(teamA, teamB);
    }
  }

  /**
   * Groups all actions by their starting_tick
   */
  private prepareActions(routines: Record<string, Battle_Action[]>): void {
    this.actionsByTick.clear();

    for (const [pokemonId, actions] of Object.entries(routines)) {
      for (const action of actions) {
        const tick = action.starting_tick;

        if (!this.actionsByTick.has(tick)) {
          this.actionsByTick.set(tick, []);
        }

        this.actionsByTick.get(tick)!.push({ pokemonId, action });
      }
    }
  }

  /**
   * Groups all events by their tick
   */
  private prepareEvents(events: BattleEvent[]): void {
    this.eventsByTick.clear();

    for (const event of events) {
      const tick = event.tick;

      if (!this.eventsByTick.has(tick)) {
        this.eventsByTick.set(tick, []);
      }

      this.eventsByTick.get(tick)!.push(event);
    }
  }

  /**
   * Creates the countdown/overtime timer UI at the top of the screen
   */
  private createOvertimeTimer(): void {
    if (!this.app) return;

    this.overtimeTimerContainer = new Container();
    this.overtimeTimerContainer.zIndex = 10000;

    // Create outer text (black stroke)
    this.overtimeTimerOuterText = new Text({
      text: "00:00",
      style: {
        fontFamily: "vck",
        fontSize: 24,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 8 },
        letterSpacing: 2,
      },
    });
    this.overtimeTimerOuterText.anchor.set(0.5);

    // Create inner text (white stroke)
    this.overtimeTimerInnerText = new Text({
      text: "00:00",
      style: {
        fontFamily: "vck",
        fontSize: 24,
        fill: 0xffffff,
        stroke: { color: 0xffffff, width: 2 },
        letterSpacing: 2,
      },
    });
    this.overtimeTimerInnerText.anchor.set(0.5);

    this.overtimeTimerContainer.addChild(
      this.overtimeTimerOuterText,
      this.overtimeTimerInnerText,
    );

    // Position at top center
    this.overtimeTimerContainer.x = this.app.screen.width / 2;
    this.overtimeTimerContainer.y = 80;

    this.app.stage.addChild(this.overtimeTimerContainer);
  }

  private createManaBars(): void {
    if (!this.app) return;

    const BAR_WIDTH = 200;
    const BAR_HEIGHT = 20;
    const MARGIN_X = 20;
    const MARGIN_Y = 20;

    // --- Team A (Left) ---
    this.manaBarA = new Container();
    this.manaBarA.zIndex = 10000;
    this.manaBarA.x = MARGIN_X;
    this.manaBarA.y = MARGIN_Y;

    const bgA = new Graphics();
    bgA.rect(0, 0, BAR_WIDTH, BAR_HEIGHT);
    bgA.fill(0x333333);
    bgA.stroke({ color: 0x000000, width: 2 });

    this.manaBarAGraphics = new Graphics();
    // Initial draw
    this.manaBarAGraphics.rect(0, 0, BAR_WIDTH, BAR_HEIGHT);
    this.manaBarAGraphics.fill(0x3366ff);

    this.manaBarAText = new Text({
      text: "50/100",
      style: {
        fontFamily: "vck",
        fontSize: 16,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 },
      },
    });
    this.manaBarAText.anchor.set(0.5);
    this.manaBarAText.x = BAR_WIDTH / 2;
    this.manaBarAText.y = BAR_HEIGHT / 2;

    this.manaBarA.addChild(bgA, this.manaBarAGraphics, this.manaBarAText);
    this.app.stage.addChild(this.manaBarA);

    // --- Team B (Right) ---
    this.manaBarB = new Container();
    this.manaBarB.zIndex = 10000;
    this.manaBarB.x = this.app.screen.width - MARGIN_X - BAR_WIDTH;
    this.manaBarB.y = MARGIN_Y;

    const bgB = new Graphics();
    bgB.rect(0, 0, BAR_WIDTH, BAR_HEIGHT);
    bgB.fill(0x333333);
    bgB.stroke({ color: 0x000000, width: 2 });

    this.manaBarBGraphics = new Graphics();
    // Initial draw
    this.manaBarBGraphics.rect(0, 0, BAR_WIDTH, BAR_HEIGHT);
    this.manaBarBGraphics.fill(0x3366ff);

    this.manaBarBText = new Text({
      text: "50/100",
      style: {
        fontFamily: "vck",
        fontSize: 16,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 },
      },
    });
    this.manaBarBText.anchor.set(0.5);
    this.manaBarBText.x = BAR_WIDTH / 2;
    this.manaBarBText.y = BAR_HEIGHT / 2;

    this.manaBarB.addChild(bgB, this.manaBarBGraphics, this.manaBarBText);
    this.app.stage.addChild(this.manaBarB);
  }

  private updateManaBars(
    currentTick: number,
    manaLogs: { teamA: Record<number, number>; teamB: Record<number, number> },
  ): void {
    const BAR_WIDTH = 200;
    const BAR_HEIGHT = 20;
    const MAX_MANA = 100;

    // Team A
    const manaA = manaLogs.teamA[currentTick] ?? 0;
    if (this.manaBarAGraphics && this.manaBarAText) {
      this.manaBarAGraphics.clear();
      const widthA = Math.max(0, (manaA / MAX_MANA) * BAR_WIDTH);
      this.manaBarAGraphics.rect(0, 0, widthA, BAR_HEIGHT);
      this.manaBarAGraphics.fill(0x3366ff);
      this.manaBarAText.text = `${Math.floor(manaA)}/${MAX_MANA}`;
    }

    // Team B
    const manaB = manaLogs.teamB[currentTick] ?? 0;
    if (this.manaBarBGraphics && this.manaBarBText) {
      this.manaBarBGraphics.clear();
      const widthB = Math.max(0, (manaB / MAX_MANA) * BAR_WIDTH);
      this.manaBarBGraphics.rect(0, 0, widthB, BAR_HEIGHT);
      this.manaBarBGraphics.fill(0x3366ff);
      this.manaBarBText.text = `${Math.floor(manaB)}/${MAX_MANA}`;
    }
  }

  /**
   * Updates the countdown timer text
   */
  private updateCountdownTimer(currentTick: number): void {
    if (!this.overtimeTimerOuterText || !this.overtimeTimerInnerText) return;

    const remainingTicks = Math.max(0, this.OVERTIME_START_TICK - currentTick);
    const remainingMs = remainingTicks * MS_PER_TICK;
    const remainingSeconds = Math.ceil(remainingMs / 1000);

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    const timeText = `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;

    this.overtimeTimerOuterText.text = timeText;
    this.overtimeTimerInnerText.text = timeText;

    // Change color as it gets closer to overtime
    let color = 0xffffff; // White
    if (remainingSeconds <= 10) {
      color = 0xff3333; // Red when under 10 seconds
    } else if (remainingSeconds <= 20) {
      color = 0xffaa00; // Orange when under 20 seconds
    }

    this.overtimeTimerOuterText.style.fill = color;
    this.overtimeTimerInnerText.style.fill = color;
  }

  /**
   * Triggers screen shake effect
   */
  private shakeScreen(intensity: number = 1, duration: number = 200): void {
    if (!this.app || this.isShaking || !this.parent) return;

    const target = this.parent;

    // Store original position only when not shaking
    if (!this.isShaking) {
      this.originalStagePosition.x = target.x;
      this.originalStagePosition.y = target.y;
    }

    // Cap intensity to reasonable values
    intensity = Math.max(0.1, Math.min(intensity, 5));
    duration = Math.max(50, Math.min(duration, 1000));

    this.isShaking = true;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      if (progress < 1) {
        // Use sine waves with decreasing amplitude for natural shake
        const amplitude = intensity * (1 - progress) * 8;
        const frequency = 0.02;

        const offsetX = Math.sin(currentTime * frequency) * amplitude;
        const offsetY =
          Math.sin(currentTime * frequency * 1.5 + Math.PI / 4) * amplitude;

        target.x = this.originalStagePosition.x + offsetX;
        target.y = this.originalStagePosition.y + offsetY;

        requestAnimationFrame(animate);
      } else {
        // Always reset to the original stored position
        target.x = this.originalStagePosition.x;
        target.y = this.originalStagePosition.y;
        this.isShaking = false;
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Shows "OVERTIME!" text that persists during overtime
   */
  private showOvertimeText(): void {
    if (!this.overtimeTimerOuterText || !this.overtimeTimerInnerText) return;

    // Update text to show "OVERTIME!"
    this.overtimeTimerOuterText.text = "OVERTIME!";
    this.overtimeTimerInnerText.text = "OVERTIME!";

    // Set color to red
    this.overtimeTimerOuterText.style.fill = 0xff3333;
    this.overtimeTimerInnerText.style.fill = 0xff3333;

    // Animate pulse once
    const duration = 1000;
    const startTime = performance.now();

    const animate = (now: number) => {
      if (
        !this.overtimeTimerContainer ||
        this.overtimeTimerContainer.destroyed
      ) {
        return;
      }

      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Pulse scale
      const pulse = 1 + Math.sin(progress * Math.PI * 2) * 0.15;
      this.overtimeTimerContainer.scale.set(pulse);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Reset to normal scale
        this.overtimeTimerContainer.scale.set(1);
      }
    };
    requestAnimationFrame(animate);
  }

  /**
   * Processes battle events at the current tick
   */
  private async processEvents(currentTick: number): Promise<void> {
    const eventsAtThisTick = this.eventsByTick.get(currentTick);
    if (!eventsAtThisTick) return;

    for (const event of eventsAtThisTick) {
      switch (event.event_type) {
        case "OVERTIME_START":
          console.log("⏰ OVERTIME HAS BEGUN!");
          this.showOvertimeText();
          break;

        case "OVERTIME_DAMAGE":
          console.log(`💥 Overtime damage: ${event.damage} to all pokemon`);

          // Screen shake based on damage amount
          const shakeIntensity = Math.min(event.damage / 5, 3); // Cap at 3 for overtime damage
          this.shakeScreen(shakeIntensity, 300);

          // Apply VFX and damage to all living pokemon
          const damagePromises: Promise<boolean | void>[] = [];

          for (const pokemon of Object.values(this.pokemons)) {
            if (pokemon.HP.current > 0 && !pokemon.IsDestroyed) {
              // Show VFX on pokemon
              if (this.app && this.parent) {
                const vfx = new VFX({
                  app: this.app,
                  parent: this.parent,
                  spriteUrl: "/assets/spells/impact/tiny-red-1.png",
                  spriteFrameCount: 9,
                  spriteScale: 1,
                  spriteScaleX: 1,
                  spriteScaleY: 1,
                  spriteAlpha: 1,
                  position: {
                    x: pokemon.Position.x,
                    y: pokemon.Position.y,
                  },
                  loops: 1,
                  speed: 1,
                  ping_pong: false,
                  anchorTarget: pokemon,
                  y_offset: 0,
                });

                damagePromises.push(vfx.load().then(() => vfx.play()));
              }

              // Apply visual damage
              damagePromises.push(pokemon.takeDamage(event.damage, true));
            }
          }

          // Wait for all VFX and damage to complete
          await Promise.all(damagePromises);
          break;

        case "KILL":
          this.onEvent?.(event);
          break;

        case "DAMAGE":
          // Regular damage events (if needed in the future)
          break;
      }
    }
  }

  /**
   * Executes a single action for a specific Pokemon
   */
  private async executeAction(
    pokemon: ClientPokemon,
    action: Battle_Action,
  ): Promise<void> {
    switch (action.type) {
      case "TURN":
        await pokemon.applyTurn(action as Battle_Action_Turn);
        break;
      case "MOVE":
        await pokemon.applyMove(action as Battle_Action_Move);
        break;
      case "USE_SKILL":
        await pokemon.applyUseSkill(action as Battle_Action_UseSkill);
        break;
      case "DANCE":
        await pokemon.applyDance(action as Battle_Action_Dance);
        break;
      case "FAINT":
        await pokemon.applyFaint(action as Battle_Action_Faint);
        break;
    }
  }

  /**
   * Waits for a fixed tick duration
   */
  private async waitTick(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, MS_PER_TICK));
  }

  /**
   * Plays all actions tick by tick in chronological order using PixiJS Ticker for better performance
   */
  async play(
    routines: Record<string, Battle_Action[]>,
    statsPerTick: BattleStatsRecordPerTick,
    events: BattleEvent[],
    manaLogs: { teamA: Record<number, number>; teamB: Record<number, number> },
  ): Promise<void> {
    this.prepareActions(routines);
    this.prepareEvents(events);

    if (this.actionsByTick.size === 0) return;

    // Create the countdown timer UI
    this.createOvertimeTimer();

    // Get the max tick
    let maxTick = 0;
    if (this.actionsByTick.size > 0) {
      maxTick = Math.max(...this.actionsByTick.keys());
      for (const actions of this.actionsByTick.values()) {
        for (const { action } of actions) {
          const duration = (action as any).total_ticks ?? 0;
          const endTick = action.starting_tick + duration;
          if (endTick > maxTick) maxTick = endTick;
        }
      }
    }
    if (this.eventsByTick.size > 0) {
      const maxEventTick = Math.max(...this.eventsByTick.keys());
      if (maxEventTick > maxTick) maxTick = maxEventTick;
    }

    console.log(
      `[BattleReplay] Starting ticker-based replay. Max tick: ${maxTick}`,
    );

    return new Promise((resolve) => {
      let currentTick = 0;
      let accumulatedTime = 0;
      let isProcessingTick = false;

      const tickerCallback = async () => {
        if (this.isDestroyed || isProcessingTick) return;

        // If we've reached the end, stop
        if (currentTick > maxTick) {
          this.app?.ticker.remove(tickerCallback);
          this.finishReplay();
          resolve();
          return;
        }

        const deltaMS = this.app?.ticker.deltaMS ?? 16.66;
        accumulatedTime += deltaMS;

        // Process as many ticks as needed to catch up
        while (accumulatedTime >= MS_PER_TICK && !isProcessingTick) {
          accumulatedTime -= MS_PER_TICK;
          isProcessingTick = true;

          try {
            // Notify listeners of tick update
            this.onTickUpdate?.(currentTick);

            // Update countdown timer
            if (currentTick < this.OVERTIME_START_TICK) {
              this.updateCountdownTimer(currentTick);
            }

            // Process status effects
            for (const pokemon of Object.values(this.pokemons)) {
              pokemon.tickStatusEffects(currentTick);
              pokemon.updateStatusEffectIcons();
            }

            // Process battle events
            // We await this because some events (like Overtime) might have animations
            await this.processEvents(currentTick);

            const actionsAtThisTick = this.actionsByTick.get(currentTick);
            if (actionsAtThisTick && actionsAtThisTick.length > 0) {
              actionsAtThisTick.forEach(({ pokemonId, action }) => {
                const pokemon = this.pokemons[pokemonId];
                if (pokemon) this.executeAction(pokemon, action);
              });
            }

            const statsAtThisTick = statsPerTick[currentTick];
            if (statsAtThisTick) {
              for (const [pokemonId, stats] of Object.entries(
                statsAtThisTick,
              )) {
                const pokemon = this.pokemons[pokemonId];
                if (!pokemon) continue;

                const previousHP = this.previousHP[pokemonId] ?? stats.hp;
                if (previousHP > stats.hp) {
                  const damageTaken = previousHP - stats.hp;
                  if (damageTaken > 20) {
                    this.shakeScreen(Math.min(damageTaken / 14, 0.5), 150);
                  }
                }
                this.previousHP[pokemonId] = stats.hp;
                pokemon.setHP(stats.hp);
                pokemon.syncShield(stats.shield);
              }
            }

            currentTick++;
          } finally {
            isProcessingTick = false;
          }

          // If currentTick > maxTick, break out of while loop
          if (currentTick > maxTick) break;
        }
      };

      this.app?.ticker.add(tickerCallback);
    });
  }

  private finishReplay(): void {
    // Set all living Pokemon back to idle animation
    for (const pokemon of Object.values(this.pokemons)) {
      if (pokemon.HP.current > 0) {
        pokemon.setAnimation(["Idle"], pokemon.CurrentDir);
      }
    }

    // Clean up timer at the end
    if (this.overtimeTimerContainer) {
      this.overtimeTimerContainer.destroy({ children: true });
    }
  }

  /**
   * Cleans up all resources and stops the replay
   */
  destroy(): void {
    this.isDestroyed = true;
    if (this.overtimeTimerContainer) {
      try {
        if (!this.overtimeTimerContainer.destroyed) {
          this.overtimeTimerContainer.destroy({ children: true });
        }
      } catch (e) {
        console.warn("[BattleReplay] Error destroying timer container:", e);
      }
      this.overtimeTimerContainer = null;
    }
  }
}
